// ╔══════════════════════════════════════════════════════════════╗
// ║  Payroll page — run, list, approve, view payslips             ║
// ╚══════════════════════════════════════════════════════════════╝

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, downloadFile, getErrorMessage } from '../lib/api.js';
import { useToast } from '../components/Toast.jsx';
import { useAuth } from '../lib/auth.jsx';
import { fmtAR, fmtMoney, STATUS_LABEL_AR, fmtDate } from '../lib/format.js';
import { Badge, Btn, Card, Field, Input, KPI, Modal } from '../components/ui.jsx';
import Icon from '../components/Icon.jsx';

const STATUS_TONES = {
  draft: 'neutral',
  calculated: 'warn',
  approved: 'success',
  paid: 'brand',
  cancelled: 'danger',
};

export default function PayrollPage() {
  const toast = useToast();
  const qc = useQueryClient();
  const { user } = useAuth();
  const canRunPayroll = user?.role === 'acc' || user?.role === 'admin';
  const [showRun, setShowRun] = useState(false);
  const [viewRun, setViewRun] = useState(null);
  const [previewing, setPreviewing] = useState(null); // month string
  const [cancelling, setCancelling] = useState(null); // run object

  const { data, isLoading } = useQuery({
    queryKey: ['payroll', 'runs'],
    queryFn: () => api.get('/payroll/runs').then((r) => r.data),
  });

  const runMut = useMutation({
    mutationFn: (month) => api.post('/payroll/runs', { month }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll'] });
      toast.push({ tone: 'success', title: 'تم تشغيل دورة الرواتب' });
      setShowRun(false);
    },
    onError: (err) =>
      toast.push({ tone: 'danger', title: 'فشل التشغيل', body: getErrorMessage(err) }),
  });

  const approveMut = useMutation({
    mutationFn: (id) => api.post(`/payroll/runs/${id}/approve`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll'] });
      toast.push({ tone: 'success', title: 'تم اعتماد الرواتب' });
    },
    onError: (err) =>
      toast.push({ tone: 'danger', title: 'فشل الاعتماد', body: getErrorMessage(err) }),
  });

  const cancelMut = useMutation({
    mutationFn: (id) => api.post(`/payroll/runs/${id}/cancel`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll'] });
      toast.push({ tone: 'success', title: 'تم إلغاء الدورة' });
      setCancelling(null);
    },
    onError: (err) =>
      toast.push({ tone: 'danger', title: 'فشل الإلغاء', body: getErrorMessage(err) }),
  });

  const redoMut = useMutation({
    mutationFn: (id) => api.post(`/payroll/runs/${id}/redo`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll'] });
      toast.push({ tone: 'success', title: 'تم إعادة حساب الدورة' });
    },
    onError: (err) =>
      toast.push({ tone: 'danger', title: 'فشل إعادة الحساب', body: getErrorMessage(err) }),
  });

  const runs = data?.runs ?? [];

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 fade-up">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-brand-500 font-bold">
            دورات الرواتب
          </div>
          <h1 className="text-2xl md:text-3xl font-black mt-1">الرواتب الشهرية</h1>
        </div>
        <div className="inline-flex items-center gap-2 flex-wrap">
          <Btn variant="outline" icon="file" onClick={() => setPreviewing(new Date().toISOString().slice(0, 7))}>
            معاينة قبل التشغيل
          </Btn>
          {canRunPayroll && (
            <Btn variant="primary" icon="zap" onClick={() => setShowRun(true)}>
              تشغيل دورة جديدة
            </Btn>
          )}
          {!canRunPayroll && (
            <Badge tone="info" dot>عرض فقط (للأدمن)</Badge>
          )}
        </div>
      </div>

      {isLoading ? (
        <Card className="p-8 text-center text-ink-400">جاري التحميل…</Card>
      ) : runs.length === 0 ? (
        <Card className="p-12 text-center">
          <Icon name="wallet" size={48} className="mx-auto text-ink-300 mb-3" />
          <p className="text-ink-500 font-bold">لا توجد دورات رواتب بعد</p>
          <p className="text-xs text-ink-400 mt-1">اضغط "تشغيل دورة جديدة" للبدء</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {runs.map((run) => (
            <Card key={run.id} className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-[11px] uppercase text-ink-400">شهر</div>
                  <div className="font-black text-xl">
                    {new Date(run.month).toLocaleDateString('ar-EG', { month: 'long', year: 'numeric' })}
                  </div>
                </div>
                <Badge tone={STATUS_TONES[run.status]} dot>
                  {STATUS_LABEL_AR[run.status]}
                </Badge>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-ink-400">عدد الموظفين</span>
                  <span className="font-bold tabular">{fmtAR(run.employeeCount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-ink-400">الإجمالي</span>
                  <span className="font-bold tabular">{fmtMoney(run.totalGross)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-ink-400">الصافي</span>
                  <span className="font-extrabold tabular text-brand-600 dark:text-brand-400">
                    {fmtMoney(run.totalNet)}
                  </span>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-ink-100 dark:border-ink-800 flex flex-wrap items-center gap-2">
                <Btn variant="outline" size="sm" icon="file" onClick={() => setViewRun(run)}>
                  التفاصيل
                </Btn>
                <Btn
                  variant="outline"
                  size="sm"
                  icon="download"
                  onClick={() =>
                    downloadFile(
                      `/payroll/runs/${run.id}/export`,
                      `payroll_${run.month.slice(0, 7)}.csv`,
                    ).catch((err) => toast.push({ tone: 'danger', title: 'فشل التحميل', body: getErrorMessage(err) }))
                  }
                >
                  Excel
                </Btn>
                {canRunPayroll && run.status !== 'approved' && run.status !== 'cancelled' && (
                  <Btn
                    variant="success"
                    size="sm"
                    icon="check"
                    loading={approveMut.isPending}
                    onClick={() => approveMut.mutate(run.id)}
                  >
                    اعتماد
                  </Btn>
                )}
                {/* Phase I: redo (recalculate) — available unless paid */}
                {canRunPayroll && run.status !== 'paid' && run.status !== 'approved' && (
                  <Btn
                    variant="outline"
                    size="sm"
                    icon="refresh"
                    loading={redoMut.isPending}
                    onClick={() => redoMut.mutate(run.id)}
                    title="إعادة حساب الدورة من جديد"
                  >
                    إعادة حساب
                  </Btn>
                )}
                {/* Phase I: cancel a calculated/approved run */}
                {canRunPayroll && run.status !== 'cancelled' && run.status !== 'paid' && (
                  <Btn
                    variant="ghost"
                    size="sm"
                    icon="x"
                    onClick={() => setCancelling(run)}
                    title="إلغاء الدورة"
                  >
                    إلغاء
                  </Btn>
                )}
              </div>
              {run.runAt && (
                <div className="text-[10px] text-ink-400 mt-2 text-end">
                  شُغّلت في {fmtDate(run.runAt)}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {showRun && (
        <RunModal
          onClose={() => setShowRun(false)}
          onRun={(m) => runMut.mutate(m)}
          loading={runMut.isPending}
        />
      )}

      {viewRun && (
        <RunDetailsModal run={viewRun} onClose={() => setViewRun(null)} />
      )}

      {previewing && (
        <PreviewModal month={previewing} onClose={() => setPreviewing(null)} />
      )}

      {cancelling && (
        <Modal
          open
          onClose={() => setCancelling(null)}
          title="إلغاء دورة الرواتب"
          size="sm"
          footer={
            <>
              <Btn variant="ghost" onClick={() => setCancelling(null)}>تراجع</Btn>
              <Btn
                variant="danger"
                icon="x"
                loading={cancelMut.isPending}
                onClick={() => cancelMut.mutate(cancelling.id)}
              >
                تأكيد الإلغاء
              </Btn>
            </>
          }
        >
          <p className="text-sm">
            هل تريد إلغاء دورة شهر{' '}
            <span className="font-bold">
              {new Date(cancelling.month).toLocaleDateString('ar-EG', { month: 'long', year: 'numeric' })}
            </span>
            ؟
          </p>
          <p className="text-xs text-ink-400 mt-2">
            سيتم حذف كل كشوف الرواتب المرتبطة بها وإرجاع أقساط السلف التى خُصمت.
            تقدر تعيد التشغيل أو الحساب لنفس الشهر بعد الإلغاء.
          </p>
        </Modal>
      )}
    </div>
  );
}

function PreviewModal({ month, onClose }) {
  const [m, setM] = useState(month);
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['payroll', 'preview', m],
    queryFn: () => api.get(`/payroll/preview/${m}`).then((r) => r.data),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="معاينة الرواتب قبل التشغيل"
      size="xl"
      footer={
        <>
          <Btn variant="ghost" onClick={onClose}>إغلاق</Btn>
        </>
      }
    >
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <Field label="الشهر">
          <Input type="month" value={m} onChange={(e) => setM(e.target.value)} dir="ltr" className="w-44"/>
        </Field>
        <Btn variant="outline" icon="check" size="sm" onClick={() => refetch()}>تحديث المعاينة</Btn>
        <span className="text-[11px] text-ink-400">
          هذه معاينة — لم يتم حفظ أي شيء بعد.
        </span>
      </div>

      {isLoading ? (
        <div className="p-8 text-center text-ink-400">جاري الحساب…</div>
      ) : !data ? null : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <KPI label="موظفين" value={fmtAR(data.employeeCount)} icon="users"/>
            <KPI label="الإجمالي" value={fmtMoney(data.totals.gross)} icon="wallet"/>
            <KPI label="الخصومات" value={fmtMoney(data.totals.deductions)} icon="trendD"/>
            <KPI label="الصافي" value={fmtMoney(data.totals.net)} icon="check" accent/>
          </div>
          <div className="overflow-x-auto rounded-xl ring-1 ring-ink-100 dark:ring-ink-800">
            <table className="w-full text-sm">
              <thead className="bg-ink-50 dark:bg-ink-800/40 text-xs text-ink-500">
                <tr>
                  {['#', 'الموظف', 'القسم', 'أساسي', 'بدلات', 'إضافي', 'مكافأة', 'خصم', 'صافي'].map((h, i) => (
                    <th key={i} className="text-start px-3 py-2 whitespace-nowrap font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-soft">
                {data.payslips.map((s) => (
                  <tr key={s.employeeId} className="hover:bg-ink-50 dark:hover:bg-ink-800/40">
                    <td className="px-3 py-2 font-mono text-[11px] text-ink-400">{s.employeeId}</td>
                    <td className="px-3 py-2 font-semibold">{s.employeeName}</td>
                    <td className="px-3 py-2">{s.deptId}</td>
                    <td className="px-3 py-2 tabular">{fmtAR(s.baseSalary)}</td>
                    <td className="px-3 py-2 tabular">{fmtAR(s.allowance)}</td>
                    <td className="px-3 py-2 tabular text-brand-600">{fmtAR(s.overtimeAmount)}</td>
                    <td className="px-3 py-2 tabular text-emerald-600">
                      +{fmtAR(Number(s.productionBonus) + Number(s.otherBonuses))}
                    </td>
                    <td className="px-3 py-2 tabular text-rose-600">
                      -{fmtAR(s.absenceDeduction + s.lateDeduction + s.advanceDeduction + s.otherDeductions + s.insurance + s.tax)}
                    </td>
                    <td className="px-3 py-2 tabular font-extrabold">{fmtAR(s.net)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Modal>
  );
}

function RunModal({ onClose, onRun, loading }) {
  const now = new Date();
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [month, setMonth] = useState(defaultMonth);

  return (
    <Modal
      open
      onClose={onClose}
      title="تشغيل دورة رواتب"
      size="md"
      footer={
        <>
          <Btn variant="ghost" onClick={onClose}>إلغاء</Btn>
          <Btn variant="primary" icon="zap" loading={loading} onClick={() => onRun(month)}>
            تشغيل
          </Btn>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="الشهر" hint="بصيغة YYYY-MM (مثلاً 2026-05)">
          <Input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            dir="ltr"
          />
        </Field>
        <p className="text-xs text-ink-400">
          سيتم احتساب رواتب جميع الموظفين النشطين بناءً على:
          الحضور · الإضافي · إنتاج القطعة · السلف · المكافآت · الخصومات.
        </p>
      </div>
    </Modal>
  );
}

function RunDetailsModal({ run, onClose }) {
  const { data, isLoading } = useQuery({
    queryKey: ['payroll', 'run', run.id],
    queryFn: () => api.get(`/payroll/runs/${run.id}`).then((r) => r.data),
  });

  const slips = data?.payslips ?? [];

  return (
    <Modal
      open
      onClose={onClose}
      title={`تفاصيل دورة ${new Date(run.month).toLocaleDateString('ar-EG', { month: 'long', year: 'numeric' })}`}
      size="xl"
    >
      {isLoading ? (
        <div className="p-8 text-center text-ink-400">جاري التحميل…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <KPI label="موظفين" value={fmtAR(slips.length)} icon="users" />
            <KPI label="إجمالي" value={fmtMoney(run.totalGross)} icon="wallet" />
            <KPI label="خصومات" value={fmtMoney(run.totalDeductions)} icon="trendD" />
            <KPI label="صافي" value={fmtMoney(run.totalNet)} icon="check" accent />
          </div>
          <div className="overflow-x-auto rounded-xl ring-1 ring-ink-100 dark:ring-ink-800">
            <table className="w-full text-sm">
              <thead className="bg-ink-50 dark:bg-ink-800/40 text-xs text-ink-500">
                <tr>
                  {['#', 'الموظف', 'أساسي', 'بدلات', 'إضافي', 'مكافآت', 'خصومات', 'الصافي'].map(
                    (h, i) => (
                      <th key={i} className="text-start font-semibold px-3 py-2 whitespace-nowrap">
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody className="divide-soft">
                {slips.map((s) => (
                  <tr key={s.id} className="hover:bg-ink-50 dark:hover:bg-ink-800/40">
                    <td className="px-3 py-2 font-mono text-[11px] text-ink-400">{s.employeeId}</td>
                    <td className="px-3 py-2 font-semibold">{s.employeeName}</td>
                    <td className="px-3 py-2 tabular">{fmtAR(s.baseSalary)}</td>
                    <td className="px-3 py-2 tabular">{fmtAR(s.allowance)}</td>
                    <td className="px-3 py-2 tabular text-brand-600">{fmtAR(s.overtimeAmount)}</td>
                    <td className="px-3 py-2 tabular text-emerald-600">
                      +{fmtAR(Number(s.productionBonus) + Number(s.otherBonuses))}
                    </td>
                    <td className="px-3 py-2 tabular text-rose-600">
                      -{fmtAR(
                        Number(s.absenceDeduction) +
                          Number(s.lateDeduction) +
                          Number(s.advanceDeduction) +
                          Number(s.otherDeductions) +
                          Number(s.insurance) +
                          Number(s.tax),
                      )}
                    </td>
                    <td className="px-3 py-2 tabular font-extrabold">{fmtAR(s.net)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Modal>
  );
}
